'use client';
import Avo from '@susu/custom-made/Avo';
import { applyBusinessRulesForStylePresetSelection } from '@susu/custom-made/utils/businessRules';
import { Clients, FabricViewMode } from '@susu/custom-made/utils/constants';
import { getCdnImageBaseUrl } from '@susu/custom-made/utils/imageDefinition';
import {
  createLayerUrlFromTemplate,
  createPreviewData,
} from '@susu/custom-made/utils/imageUrlProvider/imageUrlProvider';
import type {
  BusinessRules,
  HiddenOptionsAndValues,
  LayerDefinitions,
  Option,
  StylePreset,
  typeOptionValue,
} from '@susu/custom-made/utils/interfaces';
import { getConfigurationTypeBySection } from '@susu/custom-made/utils/navigation';
import type { LayerDefinition } from '@susu/custom-made/utils/preview/layers';
import { getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId } from '@susu/custom-made/utils/productDefinition';
import { BASE_STYLE_CAROUSEL_TEST_IDS } from '@susu/custom-made/utils/testIds';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFeatureIsOn } from '@susu/feature-flags/react';

import Button from '../button/button';
import Preview from '../preview/preview';
import styles from './baseStyleCarousel.module.scss';
import { useEnvironmentContext } from '@susu/custom-made/providers/hooks/useEnvironmentContext';
import useFabricContext from '@susu/custom-made/providers/hooks/useFabricContext';
import useUserContext from '@susu/custom-made/providers/hooks/useUserContext';
import useOptionContext from '@susu/custom-made/providers/hooks/useOptionContext';
import { useAvo } from '@susu/custom-made/hooks/useAvo';
import { FEATURE_TOGGLES } from '@susu/custom-made/feature-flags/featureToggles';
import { useI18N } from '@susu/i18n/client';

const { SHOW_LOGS, ENABLE_AVO_TRACKING } = FEATURE_TOGGLES;

const CONTAINER_CLASSES = 'mx-auto w-screen bg-gray-300 min-w-[300px] h-full flex flex-col';

const SLIDER_CONTAINER_CLASSES = 'outline-none relative h-full flex flex-col';

const SLIDE_BUTTON_CONTAINER_CLASSES = 'flex items-center justify-center';

const SLIDE_BUTTON_CONTENT_COMMON =
  'items-center whitespace-nowrap cursor-pointer text-xs leading-xs md:text-sm md:leading-sm 2xl:text-base 2xl:leading-base';

const HEADER_CLASSES =
  'flex gap-4 text-gray-800 justify-center items-center text-lg font-medium leading-base tracking-[-0.3px] pt-28 pb-14 px-0 mr-8 md:text-xl md:leading-lg md:tracking-normal md:pt-32 md:pb-56 lg:pt-28 lg:pb-8 2xl:text-2xl 2xl:leading-[34px] 2xl:pt-32 2xl:pb-[10px] 2xl:tracking-[-0.3px]';

const HEADER_IMAGE_CLASSES = 'w-24 h-24 md:w-28 md:h-28 2xl:w-[34px] 2xl:h-[34px]';

const STYLE_DESCRIPTION_CLASSES =
  'pt-16 pb-20 px-0 flex h-[68px] justify-center items-center text-xs leading-xs font-light text-gray-800 text-center max-w-[260px] m-auto md:h-[78px] md:text-sm md:leading-[21px] md:max-w-[320px]';

const BUTTON_CONTAINER_CLASSES = 'pb-28 w-[180px] m-auto md:pb-32 md:w-[200px] lg:pb-28 2xl:pb-32';

const getSlideImageContainerClasses = (isShown: boolean) => cn('m-0 relative min-h-0', isShown ? 'h-full' : 'h-0');

const getSlideButtonContentClasses = (isSelected: boolean) =>
  cn(
    SLIDE_BUTTON_CONTENT_COMMON,
    isSelected
      ? 'inline-flex text-gray-800 font-medium m-0 h-32 px-16 gap-8 shrink-0 rounded-[20px] border border-solid border-white bg-white/75 backdrop-blur-[6px] md:h-[42px] md:px-24 md:rounded-[24px] 2xl:h-[46px]'
      : 'flex text-gray-600 font-light mx-16 h-44 md:mx-24',
  );

interface PresetWithLayers {
  name: string;
  layers: LayerDefinition[];
  fallbackImage: LayerDefinition;
}

export default function Carousel({
  stylePresets,
  section,
  optionValues,
  layerDefinitions,
  businessRules,
  options,
  fabricViewMode,
}: Readonly<{
  stylePresets: StylePreset[];
  section: string;
  optionValues: typeOptionValue;
  layerDefinitions: LayerDefinitions;
  businessRules: BusinessRules;
  options: Option[];
  fabricViewMode: FabricViewMode;
}>) {
  const { t } = useI18N();
  const [selectedStyleIndex, setSelectedStyleIndex] = useState(0);
  const configurationTypeId = useMemo(() => getConfigurationTypeBySection(section), [section]);
  const { selectedFabric } = useFabricContext();
  const { product, client } = useUserContext();
  const { getAvoStandardEventParams } = useAvo();
  const {
    selectedOptions,
    hiddenOptionsAndValues,
    selectedBaseStyle,
    selectOption,
    updateHiddenOptionsAndValues,
    selectBaseStyle,
    setIsBaseStyleSelection,
  } = useOptionContext();
  const showLogsFeatureEnabled = useFeatureIsOn(SHOW_LOGS);
  const isAvoTrackingEnabled = useFeatureIsOn(ENABLE_AVO_TRACKING);
  const { cdnDomain } = useEnvironmentContext();
  const cdnImageBaseUrl = getCdnImageBaseUrl(cdnDomain);

  const applySelection = useCallback(() => {
    if (businessRules && options && optionValues && product && section && stylePresets) {
      //apply preset rules
      const tmpSelectedOptions = structuredClone(selectedOptions);
      const tmpHiddenOptionsAndValues: HiddenOptionsAndValues = structuredClone(hiddenOptionsAndValues);

      applyBusinessRulesForStylePresetSelection(
        stylePresets[selectedStyleIndex],
        tmpSelectedOptions,
        selectedFabric!.id,
        businessRules,
        tmpHiddenOptionsAndValues,
        optionValues,
        configurationTypeId,
        options,
        product,
        true,
        showLogsFeatureEnabled,
      );

      updateHiddenOptionsAndValues(tmpHiddenOptionsAndValues);
      selectOption(tmpSelectedOptions);
    }
  }, [businessRules, options, optionValues, product, section, stylePresets, selectedFabric, selectedStyleIndex]);

  const confirmSelection = (style: StylePreset) => {
    selectBaseStyle(configurationTypeId, style);
    setIsBaseStyleSelection(false);

    if (isAvoTrackingEnabled) {
      Avo.cmSelectionConfirmed({
        ...getAvoStandardEventParams(`${section}_base_style`),
        cmItemType: Avo.CmItemType.BASE_STYLE,
        currentBaseStyle: style.name.toLowerCase(),
        cmItemValue: style.name.toLowerCase(),
      });
    }
  };

  const changeStyle = (selectedStyleIndexParam: number) => {
    const currentStyleName = stylePresets[selectedStyleIndex].name;
    setSelectedStyleIndex(selectedStyleIndexParam);
    if (isAvoTrackingEnabled) {
      Avo.cmSelectionClicked({
        ...getAvoStandardEventParams(`${section}_base_style`),
        cmItemType: Avo.CmItemType.BASE_STYLE,
        currentBaseStyle: currentStyleName.toLowerCase(),
        cmItemValue: stylePresets[selectedStyleIndexParam].name.toLowerCase(),
        cmSelectedView:
          fabricViewMode === FabricViewMode.Layers ? Avo.CmSelectedView.PRODUCT_VIEW : Avo.CmSelectedView.MODEL_VIEW,
      });
    }
  };

  useEffect(() => {
    if (stylePresets && section && selectedBaseStyle[configurationTypeId]) {
      stylePresets.forEach((stylePreset, index) => {
        if (selectedBaseStyle[configurationTypeId].id === stylePreset.id) {
          setSelectedStyleIndex(index);
        }
      });
    }
  }, [selectedBaseStyle, section, stylePresets]);

  const presetsWithLayers = useMemo<PresetWithLayers[]>(() => {
    if (layerDefinitions && optionValues && selectedFabric && configurationTypeId) {
      try {
        return stylePresets.map(preset => {
          //it can happen that stylePreset is not selecting all possible option values, so we need to check the state as well in order to create full preview
          const presetOptionsRecord: Record<number, number> = structuredClone(selectedOptions[configurationTypeId]);
          preset.presetOptions.forEach(presetOption => {
            if (optionValues[presetOption.optionValueId]) {
              presetOptionsRecord[presetOption.optionId] = presetOption.optionValueId;
            }
          });

          // To handle extra configurations which have different configuration type id but using definitions from base product
          const baseConfigurationTypeId =
            getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+configurationTypeId);
          const definition = layerDefinitions.configurationTypes[baseConfigurationTypeId];

          const { fallbackUrl, layerDefinitions: imageDefinitions } = definition;

          const processedImageTemplates = imageDefinitions.map(def => {
            return createLayerUrlFromTemplate(
              def.imageTemplate,
              optionValues,
              { [configurationTypeId]: presetOptionsRecord },
              selectedFabric,
              configurationTypeId,
            );
          });

          const previewData = createPreviewData(processedImageTemplates, fallbackUrl, cdnDomain);

          return {
            name: preset.name,
            ...previewData,
          };
        });
      } catch (error) {
        console.error('Custom made - Base style preview layers Error', error);
      }
    }
    return [];
  }, [layerDefinitions, optionValues, stylePresets, selectedFabric, selectedOptions, configurationTypeId, cdnDomain]);

  useEffect(() => {
    applySelection();
  }, [selectedStyleIndex, applySelection]);

  return (
    <div
      className={cn(CONTAINER_CLASSES, {
        'pb-[34px] md:pb-20': client === Clients.inStore,
      })}
    >
      <div className={HEADER_CLASSES} data-testid={BASE_STYLE_CAROUSEL_TEST_IDS.HEADER}>
        <img
          className={HEADER_IMAGE_CLASSES}
          src={`${cdnImageBaseUrl}/svg/base_style.svg`}
          alt={t('baseStyleCarousel.baseStyleAlt')}
          height={24}
          width={24}
        />
        {t('baseStyleCarousel.chooseStyle')}
      </div>
      <div className={SLIDER_CONTAINER_CLASSES} data-testid={BASE_STYLE_CAROUSEL_TEST_IDS.CAROUSEL}>
        <div
          className={cn(styles.slideArrow, styles.slidePrev, {
            [styles.disabled]: selectedStyleIndex === 0,
          })}
          onClick={() => {
            if (selectedStyleIndex !== 0) {
              changeStyle(selectedStyleIndex - 1);
            }
          }}
        >
          {t('baseStyleCarousel.previous')}
        </div>
        <div
          className={cn(styles.slideArrow, styles.slideNext, {
            [styles.disabled]: presetsWithLayers.length === selectedStyleIndex + 1,
          })}
          onClick={() => {
            if (presetsWithLayers.length > selectedStyleIndex + 1) {
              changeStyle(selectedStyleIndex + 1);
            }
          }}
        >
          {t('baseStyleCarousel.next')}
        </div>

        {presetsWithLayers.map((style, index) => (
          <div
            className={getSlideImageContainerClasses(index === selectedStyleIndex)}
            key={style.name}
            data-testid={BASE_STYLE_CAROUSEL_TEST_IDS.getStyleImage(style.name)}
          >
            <Preview layers={style.layers} fallbackImage={style.fallbackImage} />
          </div>
        ))}

        <div className={SLIDE_BUTTON_CONTAINER_CLASSES} data-testid={BASE_STYLE_CAROUSEL_TEST_IDS.SLIDE_BUTTONS}>
          {stylePresets.map((style, index) => (
            <div
              key={style.name}
              data-testid={BASE_STYLE_CAROUSEL_TEST_IDS.getStyleId(style.id.toString())}
              data-index={index}
            >
              <div
                className={getSlideButtonContentClasses(index === selectedStyleIndex)}
                data-testid={BASE_STYLE_CAROUSEL_TEST_IDS.getStyleName(style.name)}
                onClick={() => {
                  changeStyle(index);
                }}
              >
                {style.name}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={STYLE_DESCRIPTION_CLASSES} data-testid={BASE_STYLE_CAROUSEL_TEST_IDS.DESCRIPTION}>
        {stylePresets[selectedStyleIndex].description}
      </div>
      <div className={BUTTON_CONTAINER_CLASSES}>
        <Button
          isFullSize={true}
          onClick={() => {
            confirmSelection(stylePresets[selectedStyleIndex]);
          }}
          testId={BASE_STYLE_CAROUSEL_TEST_IDS.CONFIRM_BTN}
        >
          {t('baseStyleCarousel.confirm')}
        </Button>
      </div>
    </div>
  );
}
